/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import type { UnityRootState } from "../UnityAppStore";
import { canLoadMoreOffset, nextFetchCount } from "./carouselPagination";
import type { TTilePage, TTilePaginationState } from "./carouselPagination";

/** Shared result type for loadMoreTiles thunks across all screens. */
export interface TLoadMoreTilesPayload {
  carouselId: string;
  page: TTilePage;
}

export interface TLoadMoreTilesThunkConfig {
  /** Redux action type prefix, e.g. "homescreen" → "homescreen/loadMoreTiles". */
  thunkPrefix: string;
  tag: string;
  getTileState: (
    state: UnityRootState,
    carouselId: string
  ) => TTilePaginationState | undefined;
  fetchPage: (
    carouselId: string,
    offset: number,
    count: number
  ) => Promise<TTilePage>;
  /**
   * When true, this screen shows a trailing See More tile, so tile paging is
   * capped at SEE_MORE_RAIL_TILE_CAP and the final fetch is clamped ("next =
   * cap - loaded"). When false/omitted the rail pages fully in-line as before.
   * Screens that need See More set this; screens that don't simply omit it.
   */
  isSeeMoreNeeded?: boolean;
}

/** Returns true when the error has no HTTP response (network-level failure). */
export function isNetworkError(error: unknown): boolean {
  const errorRecord = error as Record<string, unknown> | null | undefined;
  if (errorRecord === null || errorRecord === undefined) {
    return true;
  }
  const response = errorRecord.response as Record<string, unknown> | undefined;
  return response === null || response === undefined;
}

/**
 * Factory that produces a screen-specific loadMoreTiles thunk.
 * Handles the condition guard, offset/count math, fetch, and operational logging.
 * Each screen's slice still owns the extraReducers cases (pending/fulfilled/rejected).
 */
export function createLoadMoreTilesThunk(config: TLoadMoreTilesThunkConfig) {
  const isSeeMoreNeeded: boolean = config.isSeeMoreNeeded === true;

  return createAsyncThunk<
    TLoadMoreTilesPayload,
    { carouselId: string },
    { state: UnityRootState; rejectValue: string }
  >(
    `${config.thunkPrefix}/loadMoreTiles`,
    async (
      { carouselId }: { carouselId: string },
      { rejectWithValue, getState }
    ) => {
      try {
        const tileState = config.getTileState(getState(), carouselId);
        const offset = tileState === undefined ? 0 : tileState.items.length;
        // "next = cap - x" when See More is needed: a rail at 10 loaded requests
        // 5, not a full page of 10, so the total lands exactly on the cap and
        // the See More slot stays pinned. Full page size otherwise.
        const count: number = nextFetchCount(offset, isSeeMoreNeeded);
        Log.i(
          config.tag,
          `loadMoreTiles(${carouselId}) - offset=${offset}, count=${count}, seeMore=${String(isSeeMoreNeeded)}`
        );

        const fetchStartTime = Date.now();
        const page = await config.fetchPage(carouselId, offset, count);
        const fetchDuration = Date.now() - fetchStartTime;
        Log.d(
          config.tag,
          `loadMoreTiles(${carouselId}) - fetched=${page.items.length}, total=${page.resolvedCount}, durationMs=${fetchDuration}`
        );

        return { carouselId, page };
      } catch (error: unknown) {
        const message: string =
          error instanceof Error ? error.message : "Failed to load more tiles";
        Log.e(config.tag, "loadMoreTiles()", error);
        return rejectWithValue(message);
      }
    },
    {
      condition: (
        { carouselId }: { carouselId: string },
        { getState }
      ): boolean => {
        const tileState = config.getTileState(getState(), carouselId);
        // See-More-aware guard: stops paging at the cap when isSeeMoreNeeded,
        // so pagination can never render over the See More slot. When false it
        // is the original unbounded guard (page up to resolvedCount).
        return (
          tileState !== undefined &&
          canLoadMoreOffset(tileState, isSeeMoreNeeded)
        );
      }
    }
  );
}
